import { useState, useEffect, useCallback } from 'react';

interface User {
  id: string;
  piUserId: string;
  piWalletAddress: string;
  displayName: string;
  username: string;
  isNewUser: boolean;
  stats?: {
    totalGamesPlayed: number;
    totalWins: number;
    currentStreak: number;
  };
}

interface AuthResponse {
  success: boolean;
  user: User;
  token: string;
  sessionId: string;
}

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [piSDKLoaded, setPiSDKLoaded] = useState(false);

  // Environment detection
  const isDevelopment =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  const isSemiProduction =
    !isDevelopment && process.env.REACT_APP_SEMI_PRODUCTION === 'true';

  const isProduction = !isDevelopment && !isSemiProduction;

  const API_BASE_URL = 'https://koja-classics.com';

  const verifyToken = useCallback(
    async (token: string) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('authToken');
      }
    },
    [API_BASE_URL]
  );

  const handlePiSignIn = async () => {
    if (!piSDKLoaded) {
      setError('Pi Network SDK not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Starting Pi Network authentication...');

      // Authenticate with Pi Network
      const auth = await window.Pi.authenticate(
        ['username', 'payments'],
        undefined
      );

      console.log('Pi authentication successful:', auth);

      // Send authentication data to our backend
      const response = await fetch(`${API_BASE_URL}/api/auth/pi-signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          piUserId: auth.user.uid,
          piWalletAddress: auth.user.uid, // In sandbox, use uid as wallet address
          accessToken: auth.accessToken,
          displayName: auth.user.username,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Backend authentication failed:', errorData);
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data: AuthResponse = await response.json();

      // Store token and user data
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Pi authentication failed:', error);
      if (error instanceof Error) {
        if (error.message.includes('postMessage')) {
          setError(
            'Pi Network authentication failed. Please ensure you have registered your app with Pi Network for this domain.'
          );
        } else {
          setError(`Authentication failed: ${error.message}`);
        }
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSemiProdAuth = async (walletAddress: string) => {
    setLoading(true);
    setError(null);

    try {
      // Send authentication data to our backend
      const response = await fetch(`${API_BASE_URL}/api/auth/semi-prod`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress: walletAddress,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Semi-production authentication failed:', errorData);
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data: AuthResponse = await response.json();

      // Store token and user data
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('sessionId', data.sessionId);
      localStorage.setItem('userId', data.user.id);

      setUser(data.user);
      setIsAuthenticated(true);

      // Simulate network delay for realism
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Character authentication failed:', error);
      setError(
        error instanceof Error ? error.message : 'Authentication failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDevBypass = () => {
    if (!isDevelopment) return;

    // Create a mock user for development
    const mockUser: User = {
      id: 'dev-user-1',
      piUserId: 'dev-pi-user',
      piWalletAddress: 'dev-wallet-address',
      displayName: 'Dev User',
      username: 'developer',
      isNewUser: false,
      stats: {
        totalGamesPlayed: 5,
        totalWins: 3,
        currentStreak: 2,
      },
    };

    // Set mock authentication
    localStorage.setItem('authToken', 'dev-mock-token');
    setUser(mockUser);
    setIsAuthenticated(true);
    setError(null);
  };

  const handleSignOut = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userId');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check for existing authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      verifyToken(token);
    }
  }, [verifyToken]);

  return {
    // State
    isAuthenticated,
    user,
    loading,
    error,
    piSDKLoaded,
    setPiSDKLoaded,
    setError,

    // Environment flags
    isDevelopment,
    isSemiProduction,
    isProduction,

    // Actions
    handlePiSignIn,
    handleSemiProdAuth,
    handleDevBypass,
    handleSignOut,
  };
};
